import { getShopifyImageUrl } from "@/lib/shopify";
import { productImageAlt } from "@/lib/imageAlt";
import { useMemo } from "react";
import { cleanProductDescription, parseTitleAttributes } from "@/lib/productTitle";
import { areDimensionsPlausible, extractOverallDims, titleAgreesWithDims } from "@/lib/dimensions";
import { careGuidance, extractAssemblyTime, extractMaterials } from "@/lib/productSpecs";
import { DELIVERY_WINDOW_COPY } from "@/lib/shipping";

interface ProductDetailsProps {
  title: string;
  description: string;
  images: Array<{ node: { url: string; altText: string | null } }>;
  variantTitle?: string;
  /** The supplier's type, so a rug is measured as a rug here as in the Dimensions section. */
  productType?: string;
  materials?: string[];
  /** Assembly-time copy only makes sense for large furniture. */
  showAssembly?: boolean;
  /** Whether the supplier's measurements describe the selected variant (sizeFactsApply). */
  sizeFactsApply?: boolean;
}

export const ProductDetails = ({ title, description, images, variantTitle, productType, materials, showAssembly = true, sizeFactsApply = true }: ProductDetailsProps) => {

  // Full cleaned description — vendor names and spec dumps removed, never cut
  // mid-sentence.
  const fullDescription = useMemo(
    () => cleanProductDescription(description || ""),
    [description]
  );

  // Highlights — strictly derived from data we already have. Anything missing
  // is skipped rather than padded with marketing claims.
  const highlights = useMemo(() => {
    const attrs = parseTitleAttributes(title);
    const out: string[] = [];

    if (attrs.configs.length) out.push(`${attrs.configs.join(" · ")} configuration`);
    if (attrs.pieceCount) out.push(`${attrs.pieceCount} set`);
    if (attrs.functional) out.push(`${attrs.functional} function`);

    const materialLabels = Array.from(
      new Set([...(attrs.material ? [attrs.material] : []), ...(materials || [])].filter(Boolean))
    );
    if (materialLabels.length) out.push(`Material: ${materialLabels.slice(0, 3).join(", ")}`);

    if (attrs.color) out.push(`Finish: ${attrs.color}`);
    if (attrs.feature) out.push(attrs.feature.replace(/^with /i, "Includes "));

    // Another size than the measured one: only a size the variant states itself.
    const dims = extractOverallDims(sizeFactsApply ? description : undefined, variantTitle, title, productType);
    // Same checks as the Dimensions section, so the two never contradict.
    if (dims && areDimensionsPlausible(dims, title) && (!sizeFactsApply || titleAgreesWithDims(title, dims))) {
      const parts = [
        dims.Width ? `${dims.Width} W` : null,
        dims.Length ? `${dims.Length} L` : null,
        dims.Depth ? `${dims.Depth} D` : null,
        dims.Height ? `${dims.Height} H` : null,
      ].filter(Boolean);
      if (parts.length) out.push(parts.join(" × "));
    }

    return out.slice(0, 5);
  }, [title, description, variantTitle, productType, materials, sizeFactsApply]);

  // Care guidance is derived only from materials we actually parsed.
  const careLines = useMemo(() => {
    const parsed = Array.from(new Set([...(materials || []), ...extractMaterials(description || "")]));
    return careGuidance(parsed);
  }, [materials, description]);

  // Assembly time is only stated when the supplier text states it.
  const assemblyTime = useMemo(
    () => (showAssembly ? extractAssemblyTime(description || "") : null),
    [description, showAssembly]
  );

  // Pick 2 lifestyle images from the gallery (skip first which is the hero)
  const lifestyleImages = images.slice(1, 3);


  return (
    <section className="border-t border-border">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-12 md:py-20">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-start">
          {/* Text side */}
          <div>
            <p className="text-[11px] tracking-[0.2em] uppercase text-muted-foreground mb-3 font-medium">
              Details
            </p>
            <h2 className="font-sans text-[26px] md:text-[32px] leading-[1.15] font-semibold text-foreground mb-6">
              About this piece
            </h2>

            {/* Highlights — real attributes only */}
            {highlights.length > 0 && (
              <div className="mb-8">
                <h3 className="text-[16px] font-bold text-foreground mb-3">Highlights</h3>
                <ul className="space-y-1.5 text-[15px] text-muted-foreground leading-[1.7] list-disc pl-4">
                  {highlights.map((h) => (
                    <li key={h}>{h}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Description — supplier copy only. No generic filler. */}
            {fullDescription && (
              <div className="mb-8">
                <h3 className="text-[16px] font-bold text-foreground mb-3">Description</h3>
                <div className="space-y-4">
                  {fullDescription.split(/\n{2,}/).filter(Boolean).map((p, i) => (
                    <p key={i} className="text-[15px] text-muted-foreground leading-[1.7]">{p.trim()}</p>
                  ))}
                </div>
              </div>
            )}

            {/* Care — only when a material was actually parsed, keyed to it.
                Assembly time only when the supplier text states one. */}
            {(careLines.length > 0 || assemblyTime) && (
              <div className="mb-8">
                <h3 className="text-[16px] font-bold text-foreground mb-3">
                  {assemblyTime ? "Care & Assembly" : "Care"}
                </h3>
                <div className="space-y-2">
                  {careLines.map((line) => (
                    <p key={line} className="text-[15px] text-muted-foreground leading-[1.7]">{line}</p>
                  ))}
                  {assemblyTime && (
                    <p className="text-[15px] text-muted-foreground leading-[1.7]">
                      The supplier states assembly takes {assemblyTime}.
                    </p>
                  )}
                </div>
              </div>
            )}


            {/* Trust badges */}
            <div className="grid grid-cols-3 gap-6 pt-8 border-t border-border">
              <div>
                <p className="text-[13px] font-bold text-foreground mb-1">Delivery</p>
                <p className="text-[12px] text-muted-foreground leading-relaxed">{DELIVERY_WINDOW_COPY}.</p>
              </div>
              <div>
                <p className="text-[13px] font-bold text-foreground mb-1">Payment</p>
                <p className="text-[12px] text-muted-foreground leading-relaxed">Paid on the checkout page, using the options shown there (such as PayPal).</p>
              </div>
              <div>
                <p className="text-[13px] font-bold text-foreground mb-1">30-Day Returns</p>
                <p className="text-[12px] text-muted-foreground leading-relaxed">
                  From delivery. Return shipping is on us only if the item arrives damaged, defective
                  or incorrect.
                </p>
              </div>
            </div>
          </div>

          {/* Image side */}
          <div className="grid grid-cols-2 gap-3">
            {lifestyleImages.length >= 2 ? (
              <>
                <div className="aspect-[3/4] overflow-hidden bg-[#f5f0eb]">
                  <img
                    src={getShopifyImageUrl(lifestyleImages[0].node.url, 800)}
                    alt={productImageAlt(title, { suffix: "detail" })}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="aspect-[3/4] overflow-hidden bg-[#f5f0eb] mt-8">
                  <img
                    src={getShopifyImageUrl(lifestyleImages[1].node.url, 800)}
                    alt={productImageAlt(title, { suffix: "in a styled room" })}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </>
            ) : lifestyleImages.length === 1 ? (
              <div className="col-span-2 aspect-[4/3] overflow-hidden bg-[#f5f0eb]">
                <img
                  src={getShopifyImageUrl(lifestyleImages[0].node.url, 800)}
                  alt={productImageAlt(title, { suffix: "detail" })}
                  className="w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
};
